<template>
  <main-template>
    <v-row>
      <v-col cols="12" class="pb-0">
        <h2 class="text-h6 font-weight-black">Painel de indicadores</h2>
      </v-col>
    </v-row>

    <v-row>
      <e-unidades-card id="dashboard.unidades-card" />
      <e-turmas-card id="dashboard.turmas-card" :franquiaId="franquiaId" />
      <e-matriculas-totais-card id="dashboard.matriculas-totais-card" :franquiaId="franquiaId" />
      <e-taxa-ocupacao-card id="dashboard.taxa-ocupacao-card" :franquiaId="franquiaId" />
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <h2 class="text-h6 font-weight-black">Alunos</h2>
      </v-col>
    </v-row>

    <v-row>
      <e-faixa-etaria-card id="dashboard.faixa-etaria-card" :franquiaId="franquiaId" />
      <e-zona-card id="dashboard.zona-card" :franquiaId="franquiaId" />
      <e-genero-card id="dashboard.genero-card" :franquiaId="franquiaId" />
      <e-deficientes-card id="dashboard.deficientes-card" :franquiaId="franquiaId" />
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <h2 class="text-h6 font-weight-black">Professores</h2>
      </v-col>
    </v-row>

    <v-row>
      <e-quantidade-professores-card
        id="dashboard.quantidade-professores-card"
        :franquiaId="franquiaId"
      />
      <e-professor-vinculo-card id="dashboard.professor-vinculo-card" :franquiaId="franquiaId" />
      <e-professor-genero-card id="dashboard.professor-genero-card" :franquiaId="franquiaId" />
      <e-professor-deficientes-card
        id="dashboard.professor-deficientes-card"
        :franquiaId="franquiaId"
      />
    </v-row>

    <v-row id="search-school-unit-row">
      <v-col id="search-school-unit-col" cols="12">
        <e-label id="search-school-unit-label"> Filtre os indicadores por unidade escolar </e-label>
        <e-autocomplete
          :items="franquiasDisponiveis"
          :loading="carregandoFranquias"
          :return-object="false"
          :value="franquiaSelecionada"
          item-value="id"
          item-text="descricao"
          label="Selecione uma opção"
          @change="filtrarPorFranquia"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <h2 class="text-h6 font-weight-black">Resumo unidade</h2>
      </v-col>
    </v-row>

    <v-row>
      <e-matriculas-totais-card id="dashboard.matriculas-totais-card" :franquiaId="franquiaId" />
      <e-matriculas-ativas-card id="dashboard.matriculas-ativas-card" :franquiaId="franquiaId" />
      <e-aulas-lancadas-card id="dashboard.aulas-lancadas-card" :franquiaId="franquiaId" />
      <e-aulas-confirmadas-lancadas-card
        id="dashboard.aulas-confirmadas-card"
        :franquiaId="franquiaId"
      />
      <e-taxa-evasao-card id="dashboard.taxa-evasao-card" :franquiaId="franquiaId" />
    </v-row>

    <v-row class="pb-8">
      <v-col cols="12">
        <v-card id="dashboard.grafico-escola-atual" outlined>
          <apexchart type="line" height="400" :options="options" :series="series"></apexchart>
        </v-card>
      </v-col>
      <v-col v-if="podeVerGraficoDoMunicipio" cols="12">
        <v-card id="dashboard.grafico-municipio" outlined>
          <apexchart
            type="line"
            height="400"
            :options="aulasMunicipio"
            :series="dadosMunicipio"
          ></apexchart>
        </v-card>
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";

const MESES = ["Jan", "Fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const TITULO_GRAFICO_MUNICIPIO = "Gráfico de aulas de todas as escolas do município";

// Teto do eixo Y enquanto o gráfico ainda mostra os dados de exemplo.
const MAXIMO_PADRAO = 500;

function opcoesDoGrafico(titulo, maximo) {
  return {
    chart: {
      foreColor: "#263238",
      height: 350,
      type: "line",
      zoom: { enabled: false },
      toolbar: { show: true },
    },
    stroke: { width: [0, 4] },
    plotOptions: {
      bar: { columnWidth: "35%", endingShape: "rounded" },
    },
    colors: ["#0d6efd", "#212529"],
    title: {
      text: titulo,
      style: {
        fontSize: "16px",
        fontWeight: "bold",
        fontFamily: undefined,
        color: "#263238",
      },
    },
    dataLabels: { enabled: true, enabledOnSeries: [1] },
    labels: [...MESES],
    xaxis: {},
    yaxis: [
      {
        title: { text: "Lançadas" },
        max: maximo,
      },
      {
        opposite: true,
        title: { text: "Confirmadas" },
        max: maximo,
        logarithmic: false,
        logBase: 10,
        floating: false,
      },
    ],
  };
}

function seriesDeAulas(lancadas, confirmadas) {
  return [
    { name: "Aulas lançadas", type: "column", data: lancadas },
    { name: "Aulas confirmadas", type: "line", data: confirmadas },
  ];
}

// Dados de exemplo exibidos até a API responder.
function seriesDeExemplo() {
  return seriesDeAulas(
    [100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
    [23, 42, 35, 27, 43, 22, 17, 31, 22, 22, 12, 16]
  );
}

export default {
  name: "HomePage",
  async created() {
    this.sincronizarFranquiaAtiva();
    await this.carregarFranquiasDisponiveis();
    await this.graficoDeAulasPorEscola();
    if (this.podeVerGraficoDoMunicipio) await this.graficoDeAulasDoMunicipio();
  },
  data() {
    return {
      franquiaId: null,
      franquiaSelecionada: null,
      franquiasDisponiveis: [],
      carregandoFranquias: false,
      options: opcoesDoGrafico("Gráfico de aulas", MAXIMO_PADRAO),
      series: seriesDeExemplo(),
      aulasMunicipio: opcoesDoGrafico(TITULO_GRAFICO_MUNICIPIO, MAXIMO_PADRAO),
      dadosMunicipio: seriesDeExemplo(),
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    podeVerGraficoDoMunicipio() {
      return (this.user?.roles ?? []).some(
        (role) => role.name === "super-admin" || role.name === "admin"
      );
    },
    tituloDoGraficoDeAulas() {
      const franquia = this.franquiasDisponiveis.find(
        (item) => item.id === this.franquiaSelecionada
      );
      return franquia ? `Gráfico de aulas - ${franquia.descricao}` : "Gráfico de aulas";
    },
  },
  watch: {
    async user() {
      if (this.franquiaSelecionada) return;
      this.sincronizarFranquiaAtiva();
      await this.graficoDeAulasPorEscola();
    },
  },
  methods: {
    sincronizarFranquiaAtiva() {
      this.franquiaId = this.user?.franquia?.id ?? null;
      this.franquiaSelecionada = this.franquiaId;
    },

    async carregarFranquiasDisponiveis() {
      this.carregandoFranquias = true;
      this.$loaderService.open("Carregando dados");
      try {
        const franquias = await this.$services.franquiasService.listAll();
        const franquiaAtiva = this.user?.franquia;
        this.franquiasDisponiveis =
          franquiaAtiva && !franquias.some((franquia) => franquia.id === franquiaAtiva.id)
            ? [{ id: franquiaAtiva.id, descricao: franquiaAtiva.descricao }, ...franquias]
            : franquias;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoFranquias = false;
        this.$loaderService.close();
      }
    },

    async filtrarPorFranquia(franquiaId) {
      if (!franquiaId || franquiaId === this.franquiaId) return;

      this.franquiaSelecionada = franquiaId;
      this.franquiaId = franquiaId;
      await this.graficoDeAulasPorEscola();
    },

    async graficoDeAulasPorEscola() {
      if (!this.franquiaId) return;

      const { data } = await this.$services.aulasService.graficoDeAulasLancadasEConfirmadas(
        this.franquiaId
      );
      let maximo = MAXIMO_PADRAO;

      if (data.length !== 0) {
        const lancadas = data.aulasLancadas.map((aula) => aula.total);

        this.series = seriesDeAulas(
          lancadas,
          data.aulasConfirmadas.map((aula) => aula.total)
        );
        maximo = Math.max(...lancadas);
      }

      this.options = opcoesDoGrafico(this.tituloDoGraficoDeAulas, maximo);
    },

    async graficoDeAulasDoMunicipio() {
      const { data } =
        await this.$services.aulasService.graficoDeAulasLancadasEConfirmadasDoMunicipio();
      let maximo = MAXIMO_PADRAO;

      if (data.length !== 0) {
        const lancadas = data.aulasLancadas.map((aula) => aula.total);

        this.dadosMunicipio = seriesDeAulas(
          lancadas,
          data.aulasConfirmadas.map((aula) => aula.total)
        );
        maximo = Math.max(...lancadas);
      }

      this.aulasMunicipio = opcoesDoGrafico(TITULO_GRAFICO_MUNICIPIO, maximo);
    },
  },
};
</script>
